/**
 * Mock User Generator for Admin Portal
 * 
 * Generates realistic HK-based user data for demo purposes.
 * Merges with real Cognito users to show dynamic activity.
 * 
 * ENABLE_MOCK_USERS: Set to true to activate mock data
 * MOCK_USER_COUNT: Number of mock users to generate (default: 850)
 */

// Configuration
// Set to true to show mock HK users for demo purposes
// Set to false to show only real Cognito users
const ENABLE_MOCK_USERS = false; // import.meta.env.VITE_ENABLE_MOCK_USERS === 'true';
const MOCK_USER_COUNT = 850; // parseInt(import.meta.env.VITE_MOCK_USER_COUNT || '850', 10);

// HK-specific data pools
const HK_FIRST_NAMES = [
  'Wong', 'Chan', 'Lam', 'Lee', 'Cheung', 'Liu', 'Leung', 'Chow', 'Ng', 'Ho',
  'Tang', 'Yeung', 'Cheng', 'Tsang', 'Ma', 'Chiu', 'Kwok', 'Fung', 'Law', 'Poon',
  'Kwan', 'Yip', 'Siu', 'Chim', 'Yu', 'Mak', 'Chau', 'Lau', 'Hui', 'Tsui',
  'Emily', 'Jason', 'Nicole', 'Kevin', 'Michelle', 'David', 'Sarah', 'Michael', 'Jennifer', 'Christopher',
  'Chloe', 'Lucas', 'Emma', 'Oliver', 'Sophia', 'Daniel', 'Olivia', 'William', 'Ava', 'James'
];

const HK_LAST_NAMES = [
  'Wang', 'Chen', 'Lin', 'Li', 'Zhang', 'Liu', 'Liang', 'Zhou', 'Wu', 'He',
  'Tang', 'Yang', 'Zheng', 'Zeng', 'Ma', 'Zhao', 'Guo', 'Feng', 'Luo', 'Pan',
  'Guan', 'Ye', 'Xiao', 'Shen', 'Yu', 'Mai', 'Zhou', 'Liu', 'Xu', 'Cui',
  'Cheung', 'Cheng', 'Chan', 'Chow', 'Yuen', 'Lam', 'Ng', 'Wong', 'Lau', 'Leung',
  'Tse', 'Lo', 'Kwok', 'Tsang', 'Ho', 'Pang', 'Fong', 'Sze', 'Mok', 'Yiu'
];

const HK_EMAIL_DOMAINS = [
  'gmail.com', 'hotmail.com', 'yahoo.com.hk', 'outlook.com', 
  'icloud.com', 'me.com', 'live.com', 'hkymail.com'
];

const HK_SCHOOLS = [
  'Diocesan Boys\' School', 'St. Paul\'s Co-educational College', 'La Salle College',
  'Maryknoll Convent School', 'Heep Yunn School', 'St. Joseph\'s College',
  'Queen Elizabeth School', 'King\'s College', 'St. Mary\'s Canossian College',
  'Ying Wa College', 'St. Stephen\'s Girls\' College', 'Raimondi College',
  'Pui Ching Middle School', 'Wah Yan College', 'St. Francis\' Canossian College',
  'Good Hope School', 'St. Paul\'s Primary School', 'Kowloon Tong School',
  'Hong Kong International School', 'German Swiss International School'
];

const GRADE_LEVELS = ['K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];

/**
 * Generate a random integer between min and max (inclusive)
 */
function randomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

/**
 * Pick a random item from an array
 */
function randomPick(array) {
  return array[Math.floor(Math.random() * array.length)];
}

/**
 * Generate a random email based on name
 */
function generateEmail(firstName, lastName, index) {
  const cleanFirst = firstName.toLowerCase().replace(/[^a-z]/g, '');
  const cleanLast = lastName.toLowerCase().replace(/[^a-z]/g, '');
  const domain = randomPick(HK_EMAIL_DOMAINS);
  
  const patterns = [
    `${cleanFirst}.${cleanLast}${randomInt(1, 99)}@${domain}`,
    `${cleanFirst}${cleanLast}${randomInt(10, 999)}@${domain}`,
    `${cleanFirst}_${cleanLast}${randomInt(1, 999)}@${domain}`,
    `${cleanFirst}${randomInt(100, 9999)}@${domain}`,
    `${cleanFirst}${cleanLast.charAt(0)}${index}@${domain}`
  ];
  
  return randomPick(patterns);
}

/**
 * Generate a random date within the past N days
 */
function randomDateWithinPast(days) {
  const now = Date.now();
  const pastDays = randomInt(0, days);
  const timestamp = now - (pastDays * 24 * 60 * 60 * 1000);
  return new Date(timestamp).toISOString();
}

/**
 * Generate random activity status based on lastLoginAt
 */
function generateActivityStatus() {
  const random = Math.random();
  
  // Distribution: 30% active, 25% inactive, 45% zombie
  if (random < 0.30) {
    // Active: logged in within 7 days
    return { lastLoginAt: randomDateWithinPast(7), activityClass: 'active' };
  } else if (random < 0.55) {
    // Inactive: logged in 8-30 days ago
    return { lastLoginAt: randomDateWithinPast(22) + (8 * 24 * 60 * 60 * 1000), activityClass: 'inactive' };
  } else {
    // Zombie: >30 days or never
    const neverLogin = Math.random() < 0.3;
    return { 
      lastLoginAt: neverLogin ? null : randomDateWithinPast(90) + (31 * 24 * 60 * 60 * 1000), 
      activityClass: 'zombie' 
    };
  }
}

/**
 * Generate random signup progress
 */
function generateSignupProgress() {
  const random = Math.random();
  
  // Distribution: 60% complete, 15% email_pending, 10% payment_pending, 10% student_pending, 5% other
  if (random < 0.60) {
    return {
      stage: 'complete',
      stageLabel: 'Complete',
      isComplete: true,
      requiredCompleted: 3,
      requiredTotal: 3
    };
  } else if (random < 0.75) {
    return {
      stage: 'email_pending',
      stageLabel: 'Email Not Confirmed',
      isComplete: false,
      requiredCompleted: 1,
      requiredTotal: 3
    };
  } else if (random < 0.85) {
    return {
      stage: 'payment_pending',
      stageLabel: 'Payment Pending',
      isComplete: false,
      requiredCompleted: 2,
      requiredTotal: 3
    };
  } else if (random < 0.95) {
    return {
      stage: 'student_pending',
      stageLabel: 'No Student Account',
      isComplete: false,
      requiredCompleted: 2,
      requiredTotal: 4
    };
  } else {
    return {
      stage: 'email_pending',
      stageLabel: 'Email Not Confirmed',
      isComplete: false,
      requiredCompleted: 1,
      requiredTotal: 3
    };
  }
}

/**
 * Generate random user tier
 */
function generateTier() {
  const random = Math.random();
  // Distribution: 70% free, 20% premium, 10% family
  if (random < 0.70) return 'free';
  if (random < 0.90) return 'premium';
  return 'family';
}

/**
 * Generate a single mock user
 */
function generateMockUser(index) {
  const firstName = randomPick(HK_FIRST_NAMES);
  const lastName = randomPick(HK_LAST_NAMES);
  const email = generateEmail(firstName, lastName, index);
  const activity = generateActivityStatus();
  const signupProgress = generateSignupProgress();
  const tier = generateTier();
  
  // User status based on signup progress
  const status = signupProgress.stage === 'email_pending' ? 'UNCONFIRMED' : 'CONFIRMED';
  
  // Account type based on tier
  let accountType = 'individual';
  if (tier === 'family') {
    accountType = Math.random() < 0.7 ? 'family_parent' : 'family_student';
  }
  
  // Random creation date (past 365 days)
  const createdAt = randomDateWithinPast(365);
  
  // Total sessions (correlated with activity)
  const totalSessions = activity.lastLoginAt 
    ? randomInt(1, 50) 
    : 0;
  
  return {
    userId: `mock-${index}-${Date.now().toString(36)}`,
    username: email,
    email: email,
    emailVerified: status === 'CONFIRMED',
    name: `${firstName} ${lastName}`,
    firstName: firstName,
    lastName: lastName,
    phoneNumber: null,
    status: status,
    enabled: true,
    createdAt: createdAt,
    lastModified: activity.lastLoginAt || createdAt,
    lastLoginAt: activity.lastLoginAt,
    totalSessions: totalSessions,
    groups: [],
    signupProgress: signupProgress,
    tier: tier,
    accountType: accountType,
    isMock: true // Flag to identify mock users
  };
}

/**
 * Generate mock users
 * @param {number} count - Number of mock users to generate
 * @returns {Array} Array of mock user objects
 */
export function generateMockUsers(count = MOCK_USER_COUNT) {
  if (!ENABLE_MOCK_USERS) {
    return [];
  }
  
  console.log(`[MockUsers] Generating ${count} mock HK users...`);
  
  const users = [];
  const usedEmails = new Set();
  
  for (let i = 0; i < count; i++) {
    let user = generateMockUser(i);
    
    // Ensure unique email
    let attempts = 0;
    while (usedEmails.has(user.email) && attempts < 10) {
      user = generateMockUser(i + attempts * 1000);
      attempts++;
    }
    
    if (!usedEmails.has(user.email)) {
      usedEmails.add(user.email);
      users.push(user);
    }
  }
  
  console.log(`[MockUsers] Generated ${users.length} unique mock users`);
  
  return users;
}

/**
 * Merge mock users with real users
 * @param {Array} realUsers - Array of real users from Cognito
 * @param {Array} mockUsers - Array of mock users
 * @returns {Array} Merged array with no duplicates
 */
export function mergeUsers(realUsers = [], mockUsers = []) {
  if (!ENABLE_MOCK_USERS) {
    return realUsers;
  }
  
  const emailSet = new Set();
  const mergedUsers = [];
  
  // Add real users first (priority)
  realUsers.forEach(user => {
    if (user.email && !emailSet.has(user.email.toLowerCase())) {
      emailSet.add(user.email.toLowerCase());
      mergedUsers.push({ ...user, isMock: false });
    }
  });
  
  // Add mock users if email not already present
  mockUsers.forEach(user => {
    if (user.email && !emailSet.has(user.email.toLowerCase())) {
      emailSet.add(user.email.toLowerCase());
      mergedUsers.push(user);
    }
  });
  
  console.log(`[MockUsers] Merged ${realUsers.length} real + ${mockUsers.length} mock = ${mergedUsers.length} total users`);
  
  return mergedUsers;
}

/**
 * Check if mock users are enabled
 */
export function isMockEnabled() {
  return ENABLE_MOCK_USERS;
}

/**
 * Get mock user count configuration
 */
export function getMockUserCount() {
  return MOCK_USER_COUNT;
}

// Export configuration for debugging
export const MOCK_CONFIG = {
  enabled: ENABLE_MOCK_USERS,
  count: MOCK_USER_COUNT,
  region: 'Hong Kong',
  locales: ['zh-HK', 'en']
};
